import React, { useMemo } from 'react';
import MediaCover from '@opm/components/MediaCover';
import ChannelLabel from '@op/components/ChannelLabel';
import BubbleCommon from '@op/components/BubbleCommon';
import TextClamp from '@opm/components/TextClamp';
import URLShare from '@opm/components/URLShare';
import CountryItem from '@opm/pages/Hashtag/components/CountryItem';
import { transLanguage } from '@op/utils';
import moment from 'moment';
import './index.scss';
import useIntls from '@opm/hooks/useIntls';

/** 底部描述项类型 */
export type TDesItemType = 'region' | 'language' | 'time' | 'text';

/** 底部描述区单项配置 */
export interface IDesItemConfig {
  /** 展示类型 */
  type: TDesItemType;
  /**
   * 数据字段名（从 data 中取值），默认与 type 相同。
   * - 'text' 类型支持双语映射：`{ en: 'xxx_en', zh: 'xxx' }`，按当前语言取值
   * - 也可传入函数 `(data) => value`，直接返回要展示的值
   */
  field?: string | Record<'en' | 'zh', string> | ((data: any) => any);
  /** 是否展示分隔竖线 "|"，默认 true */
  showLine?: boolean;
  /** 是否展示文案（默认仅图标），适用于 region/language */
  showText?: boolean;
  /** 时间格式化字符串，仅 type='time' 有效，默认 'YYYY-MM-DD HH:mm' */
  format?: string;
  /** 自定义 className 后缀；最终为 dbom-videobox-des-text-{className}，便于单项样式定制 */
  className?: string;
}

/** 视频卡片字段映射配置 */
export interface IVideoFieldMapping {
  /** 封面图字段名，默认 'cover' */
  cover?: string | ((data: any) => string);
  /** 视频时长字段名，默认 'duration' */
  duration?: string;
  /**
   * 标题内容
   * - string: 从 data[title] 读取
   * - function: 调用 title(data) 获取
   */
  title?: string | ((data: any) => string);
  /**
   * 渠道名称（传给 ChannelLabel 的 channel_name）
   * - string: 从 data[channelName] 读取
   * - function: 直接使用返回值（如 () => 'twitter'）
   */
  channelName?: string | ((data: any) => any);
  /** 作者/用户名字段名，默认 'username' */
  authorName?: string;
  /** 国家字段名，默认 'country'。region 类型会以此为 field */
  country?: string;
  /** 发布时间字段名，默认 'time' */
  publishTime?: string;
  /** 视频链接字段名，默认 'url' */
  videoUrl?: string;
  /** 语言字段名，默认 'language' */
  language?: string;
}

/** 可隐藏的元素 */
export type THideKey =
  | 'duration'
  | 'share'
  | 'channel'
  | 'country'
  | 'time';

/** 标题渲染模式 */
export type TTitleMode = 'textClamp' | 'bubble';

interface IVideoInfoCellProps {
  /** 行数据 */
  data: any;
  /** 字段映射，不传则使用默认值 */
  fieldMapping?: IVideoFieldMapping;
  /** 隐藏指定元素 */
  hideKeys?: THideKey[];
  /**
   * 底部描述区配置数组，按顺序渲染。
   * 传入后忽略 hideKeys 中 country/time 的控制
   */
  desItems?: IDesItemConfig[];
  /**
   * 标题渲染模式
   * - textClamp: 溢出省略 + Bubble 悬浮展示（默认）
   * - bubble: BubbleCommon 包裹展示
   */
  titleMode?: TTitleMode;
  /** TextClamp 最大行数（仅 titleMode='textClamp' 时生效），默认 2 */
  titleRowNum?: number;
  /** 时长角标的额外 className */
  durationClassName?: string; 
  /** 封面图点击事件 */
  coverImageOnClick?: () => void;
  /** 渠道标签点击事件 */
  channelLabelOnClick?: () => void;
}

/** 默认字段映射 */
const DEFAULT_FIELD_MAPPING: Required<Omit<IVideoFieldMapping, 'title' | 'channelName'>> & {
  title: string;
  channelName: string;
} = {
  cover: 'cover',
  duration: 'duration',
  title: '',
  channelName: 'channel',
  authorName: 'username',
  country: 'country',
  publishTime: 'time',
  videoUrl: 'url',
  language: 'language',
};

/** 从 fieldMapping 解析值 */
function resolveField(
  mapping: string | ((data: any) => any) | undefined,
  data: any,
  defaultField: string,
): any {
  if (mapping === undefined) {
    return data?.[defaultField];
  }
  if (typeof mapping === 'function') {
    return mapping(data);
  }
  return data?.[mapping];
}

/** 解析标题文本 */
function resolveTitle(
  titleMapping: string | ((data: any) => string) | undefined,
  data: any,
): string {
  if (titleMapping === undefined) {
    return data?.content_text || '';
  }
  if (typeof titleMapping === 'function') {
    return titleMapping(data);
  }
  return data?.[titleMapping] || '';
}

export default function VideoInfoCell({
  data,
  fieldMapping,
  hideKeys = [],
  desItems,
  titleMode = 'textClamp',
  titleRowNum = 2,
  durationClassName = 'dbom-videobox-time',
  coverImageOnClick,
  channelLabelOnClick,
}: IVideoInfoCellProps) {
  const { isEn, intl } = useIntls();
  const merged = useMemo(
    () => ({ ...DEFAULT_FIELD_MAPPING, ...fieldMapping }),
    [fieldMapping],
  );
  const showDuration = !hideKeys.includes('duration') && merged.duration;
  const showShare = !hideKeys.includes('share');
  const showChannel =
    !hideKeys.includes('channel') && (merged.channelName || merged.authorName);

  const coverUrl = resolveField(merged.cover, data, 'cover');
  const duration = resolveField(
    merged.duration,
    data,
    DEFAULT_FIELD_MAPPING.duration,
  );
  const titleText = resolveTitle(merged.title, data);
  const channelName = resolveField(
    merged.channelName,
    data,
    DEFAULT_FIELD_MAPPING.channelName,
  );
  const authorName = resolveField(
    merged.authorName,
    data,
    DEFAULT_FIELD_MAPPING.authorName,
  );
  const videoUrl = resolveField(
    merged.videoUrl,
    data,
    DEFAULT_FIELD_MAPPING.videoUrl,
  );


  /** 最终的 desItems：若显式传入则直接使用，否则从 hideKeys 兼容生成 */
  const effectiveDesItems = useMemo((): IDesItemConfig[] => {
    if (desItems) return desItems;

    const items: IDesItemConfig[] = [];
    if (!hideKeys.includes('country')) {
      items.push({ type: 'region', field: merged.country, showText: true });
    }
    if (!hideKeys.includes('time')) {
      items.push({ type: 'time', field: merged.publishTime });
    }
    return items;
  }, [desItems, merged, intl]);

  /** 渲染单个 des 项 */
  const renderDesItem = (cfg: IDesItemConfig, idx: number) => {
    const {
      type,
      field,
      showLine = true,
      showText = false,
      format = 'YYYY-MM-DD HH:mm',
      className: extraCls,
    } = cfg;

    // 解析字段名：支持 string、{ en, zh } 双语映射或函数
    const rawValue = (() => {
      if (typeof field === 'function') {
        return field(data);
      }
      const fieldKey = (() => {
        if (typeof field === 'object' && field !== null) {
          return field[isEn ? 'en' : 'zh'] || field[isEn ? 'zh' : 'en'] || type;
        }
        return field || type;
      })();
      return data?.[fieldKey];
    })();


    // text 允许空值通过，内部处理回退逻辑
    if (
      type !== 'text' &&
      (rawValue === undefined || rawValue === null || rawValue === '')
    )
      return null;

    const cls = [
      'dbom-videobox-text',
      showLine ? 'dbom-videobox-text--line' : 'dbom-videobox-text--no-line',
      `dbom-videobox-des-${type}`,
      extraCls && `dbom-videobox-des-${type}-${extraCls}`,
    ]
      .filter(Boolean)
      .join(' ');

    switch (type) {
      case 'region':
        return (
          <CountryItem
            key={idx}
            className={cls}
            iconStyle={{ width: 16 }}
            country={String(rawValue)}
            showText={showText}
          />
        );
      case 'language':
        return (
          <span key={idx} className={cls}>
            <span className="dbom-videobox-text--ellipsis">{transLanguage(String(rawValue), isEn ? 'en-US' : 'zh-CN') || rawValue}</span>
          </span>
        );
      case 'time':
        return (
          <span key={idx} className={cls}>
            {moment(rawValue).format(format)}
          </span>
        );
      case 'text': {
        // 若当前语言字段为空，回退到另一语言字段（使用 desItems 的 field 配置）
        let text = rawValue;
        if (
          (text === undefined || text === null || text === '') &&
          typeof field === 'object' &&
          field !== null
        ) {
          const fallbackKey = field[isEn ? 'zh' : 'en'];
          text = fallbackKey ? data?.[fallbackKey] : null;
        }
        if (text === undefined || text === null || text === '') return null;
        return (
          <span key={idx} className={cls}>
            <span className="dbom-videobox-text--ellipsis">{String(text)}</span>
          </span>
        );
      } 
      default:
        return null;
    }
  };

  const desContent = effectiveDesItems.map(renderDesItem).filter(Boolean);

  return (
    <div className="dbom-videobox">
      <MediaCover
        coverUrl={coverUrl}
        duration={duration}
        showDuration={!!showDuration}
        className="dbom-videobox-coverbox"
        coverClassName="dbom-videobox-cover"
        durationClassName={durationClassName}
        onClick={coverImageOnClick}
      />
      <div className="dbom-videobox-infobox">
        {titleMode === 'bubble' ? (
          <div className="dbom-videobox-title">
            <BubbleCommon
              showIcon={false}
              placement="top"
              tipsMap={null}
              content={titleText}
            >
              {titleText}
            </BubbleCommon>
          </div>
        ) : (
          <TextClamp
            rowNum={titleRowNum}
            text={titleText}
            placement="top"
            className="dbom-videobox-title"
          />
        )}
        {showShare && videoUrl && (
          <URLShare
            url={videoUrl}
            className="dbom-videobox-share"
            iconClassName="dbo-feeds-btn-icon"
          />
        )}
        {showChannel && (
          <div className="dbom-videobox-source" onClick={() => {
            channelLabelOnClick?.();
          }}>
            <ChannelLabel
              replaceText={authorName}
              iconStyle={{ width: 16, height: 16 }}
              channel_name={channelName}
            />
          </div>
        )}
        {desContent.length > 0 && (
          <div className="dbom-videobox-des">{desContent}</div>
        )}
      </div>
    </div>
  );
}
